import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { AlertTriangle, ChevronRight, Laptop, Loader2, Plus, RefreshCw, Search, X } from "lucide-react";
import { toast } from "sonner";
import api from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { ASSET_TYPES, canManageAssets, money, partyText, procurementError, typeLabel } from "../lib/procurement";
import { ExportButton, LoanNote, StatusBadge, WarrantyNote } from "../components/procurement/ProcurementBits";
import { Button } from "../components/ui/button";
import { Card } from "../components/ui/card";
import { Input } from "../components/ui/input";
import { Skeleton } from "../components/ui/skeleton";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";

const ALL = "__all__";
const PAGE_SIZE = 50;
const STATUS_TABS = [["active", "En service"], ["in_stock", "En stock"], ["assigned", "Affectés"], ["loaned", "Prêtés"], ["retired", "Retirés"]];

function useFilters() {
  const [params, setParams] = useSearchParams();
  const filters = {
    status: STATUS_TABS.some(([id]) => id === params.get("status")) ? params.get("status") : "active",
    type: ASSET_TYPES.some(([id]) => id === params.get("type")) ? params.get("type") : "",
    overdue: params.get("overdue") === "1",
    toConfirm: params.get("owner") === "to_confirm",
    warranty: ["ending", "expired"].includes(params.get("warranty")) ? params.get("warranty") : "",
    holder: params.get("holder") || "",
    search: params.get("q") || "",
  };
  const update = useCallback((changes) => {
    setParams((prev) => {
      const next = new URLSearchParams(prev);
      for (const [key, value] of Object.entries(changes)) {
        const name = { search: "q", toConfirm: "owner" }[key] || key;
        const out = key === "overdue" ? (value ? "1" : "") : key === "toConfirm" ? (value ? "to_confirm" : "") : value;
        if (out === "" || out == null) next.delete(name);
        else next.set(name, out);
      }
      return next;
    }, { replace: true });
  }, [setParams]);
  return [filters, update];
}

function Chip({ active, onClick, children, testid }) {
  return (
    <button type="button" onClick={onClick} aria-pressed={active} data-testid={testid}
      className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${active ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
      {children}
    </button>
  );
}

export default function ProcurementAssets() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [filters, update] = useFilters();
  const [searchInput, setSearchInput] = useState(filters.search);
  const [data, setData] = useState({ items: [], total: 0, counts: null });
  const [phase, setPhase] = useState("loading");
  const [error, setError] = useState("");
  const [loadingMore, setLoadingMore] = useState(false);
  const seq = useRef(0);
  const canCreate = canManageAssets(user);

  const latest = useRef({ filters, update });
  latest.current = { filters, update };
  useEffect(() => {
    const t = setTimeout(() => {
      const { filters: current, update: apply } = latest.current;
      if (searchInput.trim() !== current.search) apply({ search: searchInput.trim() });
    }, 300);
    return () => clearTimeout(t);
  }, [searchInput]); // eslint-disable-line react-hooks/exhaustive-deps

  const query = useMemo(() => {
    const out = { limit: PAGE_SIZE, status: filters.status };
    if (filters.type) out.type = filters.type;
    if (filters.overdue) out.overdue = true;
    if (filters.toConfirm) out.owner_kind = "to_confirm";
    if (filters.warranty) out.warranty = filters.warranty;
    if (filters.holder) out.holder = filters.holder;
    if (filters.search) out.q = filters.search;
    return out;
  }, [filters.status, filters.type, filters.overdue, filters.toConfirm, filters.warranty, filters.holder, filters.search]);

  const load = useCallback(async () => {
    const mine = ++seq.current;
    setPhase("loading");
    try {
      const { data: body } = await api.get("/procurement/assets", { params: query });
      if (mine !== seq.current) return;
      setData(body);
      setPhase("ready");
    } catch (e) {
      if (mine !== seq.current) return;
      setError(procurementError(e, "Impossible de charger l'inventaire."));
      setPhase("error");
    }
  }, [query]);
  useEffect(() => { load(); return () => { seq.current += 1; }; }, [load]);

  const loadMore = async () => {
    setLoadingMore(true);
    try {
      const { data: body } = await api.get("/procurement/assets", { params: { ...query, skip: data.items.length } });
      setData((prev) => ({ ...body, items: [...prev.items, ...body.items.filter((i) => !prev.items.some((p) => p.id === i.id))] }));
    } catch (e) {
      toast.error(procurementError(e, "Impossible de charger la suite."));
    } finally {
      setLoadingMore(false);
    }
  };

  const costs = data.sees_costs;
  const counts = data.counts;
  const filtered = !!(filters.type || filters.overdue || filters.toConfirm || filters.warranty || filters.holder || filters.search);
  const reset = () => { setSearchInput(""); update({ type: "", overdue: false, toConfirm: false, warranty: "", holder: "", search: "" }); };
  const countOf = (id) => (counts ? (id === "active" ? counts.in_stock + counts.assigned + counts.loaned : counts[id]) : null);

  return (
    <div className="space-y-4" data-testid="procurement-assets-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="font-display font-bold text-2xl text-slate-900">Actifs de l'entreprise</h1>
          <p className="text-sm text-slate-500">Le matériel et les licences de Jamora : qui l'a payé, à qui il appartient, qui l'a entre les mains.</p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <ExportButton kind="assets" />
          {canCreate && (
            <Button asChild data-testid="asset-new"><Link to="/achats/actifs/nouveau"><Plus size={16} className="mr-1.5" />Nouvel actif</Link></Button>
          )}
        </div>
      </div>

      <div className="flex gap-1 flex-wrap" role="tablist" aria-label="Situation des actifs">
        {STATUS_TABS.map(([id, label]) => (
          <button key={id} type="button" role="tab" aria-selected={filters.status === id} onClick={() => update({ status: id === "active" ? "" : id })} data-testid={`assets-status-${id}`}
            className={`rounded-full px-3 py-1.5 text-sm font-medium whitespace-nowrap ${filters.status === id ? "bg-jamora text-white" : "bg-white text-slate-600 border border-slate-200 hover:bg-slate-50"}`}>
            {label}{countOf(id) != null && <span className="ml-1.5 tabular-nums opacity-80">{countOf(id)}</span>}
          </button>
        ))}
      </div>

      <div className="flex items-center gap-2 flex-wrap">
        <div className="relative basis-full sm:basis-auto sm:flex-1 min-w-0 md:max-w-xs">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" aria-hidden="true" />
          <Input value={searchInput} onChange={(e) => setSearchInput(e.target.value)} placeholder="Numéro, marque, modèle, emplacement…" className="pl-9 h-9"
            aria-label="Rechercher un actif" data-testid="assets-search" />
        </div>
        <Select value={filters.type || ALL} onValueChange={(v) => update({ type: v === ALL ? "" : v })}>
          <SelectTrigger className="h-9 w-[calc(50%-4px)] sm:w-48" aria-label="Type d'actif" data-testid="assets-type"><SelectValue /></SelectTrigger>
          <SelectContent>
            <SelectItem value={ALL}>Tous les types</SelectItem>
            {ASSET_TYPES.map(([id, label]) => <SelectItem key={id} value={id}>{label}</SelectItem>)}
          </SelectContent>
        </Select>
        {filtered && <Button variant="ghost" size="sm" className="h-9 text-slate-600" onClick={reset}><X size={14} className="mr-1" />Effacer</Button>}
      </div>
      <div className="flex gap-1 flex-wrap" aria-label="Filtres rapides">
        <Chip active={filters.overdue} onClick={() => update({ overdue: !filters.overdue })} testid="assets-chip-overdue">
          Prêts en retard{counts ? <span className="ml-1.5 tabular-nums opacity-80">{counts.loans_overdue}</span> : null}
        </Chip>
        <Chip active={filters.toConfirm} onClick={() => update({ toConfirm: !filters.toConfirm })} testid="assets-chip-owner">Propriétaire à confirmer</Chip>
        <Chip active={filters.warranty === "ending"} onClick={() => update({ warranty: filters.warranty === "ending" ? "" : "ending" })} testid="assets-chip-warranty">Garantie bientôt finie</Chip>
      </div>

      {phase === "loading" && <div className="space-y-2"><Skeleton className="h-12 w-full" /><Skeleton className="h-12 w-full" /></div>}
      {phase === "error" && (
        <Card className="p-6 text-center space-y-3">
          <AlertTriangle className="mx-auto text-amber-500" size={20} />
          <p className="text-sm text-slate-700">{error}</p>
          <Button variant="outline" onClick={load}><RefreshCw size={14} className="mr-1.5" />Réessayer</Button>
        </Card>
      )}
      {phase === "ready" && data.items.length === 0 && (
        <Card className="p-8 text-center space-y-2" data-testid="assets-empty">
          <Laptop className="mx-auto text-slate-300" size={26} />
          {filtered ? (
            <><h3 className="font-medium text-slate-900">Aucun actif ne correspond</h3><Button variant="outline" size="sm" onClick={reset}>Effacer les filtres</Button></>
          ) : (
            <><h3 className="font-medium text-slate-900">Aucun actif {filters.status === "retired" ? "retiré" : "pour le moment"}</h3>
              <p className="text-sm text-slate-500 max-w-md mx-auto">
                {canCreate ? "Ajoutez le premier : ordinateur, téléphone, licence… Vous noterez qui l'a payé, à qui il appartient et qui l'a." : "Les actifs de l'entreprise apparaîtront ici."}
              </p>
              {canCreate && filters.status !== "retired" && <Button asChild size="sm"><Link to="/achats/actifs/nouveau"><Plus size={14} className="mr-1.5" />Nouvel actif</Link></Button>}</>
          )}
        </Card>
      )}
      {phase === "ready" && data.items.length > 0 && (
        <>
          <Card className="hidden md:block overflow-hidden" data-testid="assets-table">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Actif</TableHead><TableHead>Situation</TableHead><TableHead>Détenteur</TableHead>
                  <TableHead className="hidden lg:table-cell">Propriétaire</TableHead><TableHead className="hidden lg:table-cell">Emplacement</TableHead>
                  {costs && <TableHead className="hidden xl:table-cell text-right">Coût</TableHead>}
                </TableRow>
              </TableHeader>
              <TableBody>
                {data.items.map((a) => (
                  <TableRow key={a.id} className="cursor-pointer" onClick={() => navigate(`/achats/actifs/${a.id}`)} data-testid={`asset-row-${a.number}`}>
                    <TableCell>
                      <Link to={`/achats/actifs/${a.id}`} className="font-medium text-slate-900 hover:underline" onClick={(e) => e.stopPropagation()}>{a.label}</Link>
                      <div className="text-xs text-slate-500 tabular-nums">{a.number} · {typeLabel(a.type)}</div>
                    </TableCell>
                    <TableCell>
                      <div className="flex flex-col items-start gap-0.5"><StatusBadge status={a.status} /><LoanNote asset={a} /><WarrantyNote asset={a} /></div>
                    </TableCell>
                    <TableCell className="text-sm">{a.status === "retired" ? <span className="text-slate-400">—</span> : partyText(a.holder)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{partyText(a.owner)}</TableCell>
                    <TableCell className="hidden lg:table-cell text-sm">{a.location || <span className="text-slate-400">—</span>}</TableCell>
                    {costs && <TableCell className="hidden xl:table-cell text-sm tabular-nums text-right">{money(a.cost_minor, a.currency) || <span className="text-slate-400">—</span>}</TableCell>}
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Card>
          <Card className="md:hidden divide-y divide-slate-100" data-testid="assets-cards">
            {data.items.map((a) => (
              <Link key={a.id} to={`/achats/actifs/${a.id}`} className="flex items-start gap-3 px-4 py-3" data-testid={`asset-card-${a.number}`}>
                <div className="min-w-0 flex-1 space-y-1">
                  <div className="font-medium text-slate-900">{a.label}</div>
                  <div className="text-xs text-slate-500">{a.number} · {typeLabel(a.type)}</div>
                  <div className="flex flex-wrap items-center gap-x-2 gap-y-1 text-sm">
                    <StatusBadge status={a.status} />
                    {a.status !== "retired" && <span className="text-slate-700">{partyText(a.holder)}</span>}
                  </div>
                  <div className="flex flex-wrap gap-x-3"><LoanNote asset={a} /><WarrantyNote asset={a} /></div>
                </div>
                <ChevronRight size={16} className="text-slate-300 shrink-0 mt-1" />
              </Link>
            ))}
          </Card>
          <div className="flex items-center justify-between gap-3 text-xs text-slate-500">
            <span data-testid="assets-count">{data.items.length} sur {data.total} actif{data.total > 1 ? "s" : ""}</span>
            {data.items.length < data.total && (
              <Button variant="outline" size="sm" onClick={loadMore} disabled={loadingMore}>{loadingMore && <Loader2 size={14} className="mr-1.5 animate-spin" />}Charger plus</Button>
            )}
          </div>
        </>
      )}
    </div>
  );
}
